import React, { useMemo } from "react";
import { Icon } from "semantic-ui-react";
import {
  formatDateRange,
  getLocale,
  getRandomItems,
  getShortDate,
} from "../../../../utils/helperFunctions";
import useTheme from "../../../../hooks/useTheme";
import { AppBrowserView, AppColumn, AppRow } from "../../../UI/Layouts";
import PhotoGrid from "../../../UI/PhotoGrid";
import { MobileView } from "../../../UI/ResponsiveContext";
import { SizedBox } from "../../../UI/Sizes";
import { MODAL_MEDIA_VIEW } from "../../../MediaViewer/MediaViewModal";
import AppText from "../../../UI/Texts/AppTexts";
import { useSelector } from "react-redux";
import { t } from "i18next";
import { getFeeMatrixForWorkplace } from "../../../../utils/feeFunctions";

const randomMedicalPlaces = [
  "https://w0.peakpx.com/wallpaper/127/781/HD-wallpaper-cities-water-night-city-building-reflection-stockholm-sweden.jpg",
  "https://w0.peakpx.com/wallpaper/289/577/HD-wallpaper-cities-stockholm-sweden.jpg",
  "https://wallpaper.forfun.com/fetch/0b/0baab85b774046e7caea8b58d2636047.jpeg",
  "https://upload.wikimedia.org/wikipedia/commons/thumb/3/34/Swedish_Medical_Center-FrontSide.jpg/800px-Swedish_Medical_Center-FrontSide.jpg",
  "https://oncoblotlabs.com/img/swedishmedicalcenterballard.jpg",
  "https://media.bizj.us/view/img/7783712/c01sfh-aerial*1200xx8000-6002-0-291.jpg",
  "https://vertexeng.com/app/uploads/2018/08/46156-Tenant-Improvement-Swedish-Medical-Center-CO-3-1024x684.jpeg",
  "https://jedunn.com/wp-content/uploads/2024/04/hca-swedish-medical-center-nurses-station.jpg",
  "https://www.collinswoerman.com/sites/default/files/2021-10/Swedish-Issaquah-2_0.jpg",
];

function getRandomNumber() {
  const numbers = [1, 2, 3, 4];
  const randomIndex = Math.floor(Math.random() * numbers.length);
  return numbers[randomIndex];
}

export function JobHeader({
  jobData,
  openModal,
  staffingWorkplaceData,
}) {
  const theme = useTheme();
  const coreConstants = useSelector((state) => state.core.constants);
  const feeConfigs = useSelector((state) => state.core.feeConfigs) ?? {};

  const photos = useMemo(() => {
    const attachments = (jobData?.attachments ?? []).map((att) => att.fileLink);
    return (
      attachments.length > 0
        ? attachments.slice(0, 4)
        : (
            jobData?.images?.map((img) => img.link) ??
            getRandomItems(randomMedicalPlaces, getRandomNumber())
          )
            .slice(0, 4)
            .reverse()
    );
  }, [jobData?.attachments, jobData?.images]);

  const { currentUser } = useSelector((state) => state.auth);
  const isAuthenticated = currentUser != null;

  const healthcareWorkplaceDetails =
    jobData?.healthcareWorkplaceDetails ?? jobData?.workplaceDetails;

  if (!healthcareWorkplaceDetails) {
    return <div></div>;
  }

  const actualWorkplaceDetails = isAuthenticated
    ? healthcareWorkplaceDetails
    : (jobData.workplaceDetails ?? healthcareWorkplaceDetails);

  const jobCity =
    healthcareWorkplaceDetails.city &&
    healthcareWorkplaceDetails.city.length > 0
      ? healthcareWorkplaceDetails.city
      : healthcareWorkplaceDetails.municipality;

  const startAt = jobData?.startAt ?? 0;
  const endAt = jobData?.endAt ?? 0;

  const expireAt = jobData?.expireAt ?? 0;

  const showDateRange = startAt > 1 && endAt > 1;

  const showExpireAt = expireAt > 1;

  const professionTranslation = (() => {
    const roles = coreConstants.workroles ?? [];

    if (roles.length > 0) {
      return (
        roles.find((e) => e.id === jobData.profession)?.text[getLocale()] ??
        jobData.profession
      );
    }

    return jobData.profession;
  })();

  const profession =
    professionTranslation && professionTranslation.length > 0
      ? professionTranslation
      : jobData.profession;

  const allEmploymentTypes = coreConstants.employmentTypes ?? [];

  const jobEmploymentTypes = (jobData?.employmentTypes ?? [])?.map((et) => {
    const appEmploymentType = allEmploymentTypes.find((e) => e.id === et);
    const text = appEmploymentType?.text[getLocale()] ?? et;
    return text;
  });

  const jobTitle = `${profession} ${t("to")} ${jobCity}`;

  const dateRange =
    startAt > 1 && endAt > 1
      ? `| ${formatDateRange(startAt, endAt, false, t("to"))}`
      : "";

  const baseFee = jobData?.baseFee?.amount ?? 0;

  const { canDisplayCompensation, text: compensationText } =
    getFeeMatrixForWorkplace({
      baseJobFeeAmount: baseFee,
      workplaceId: jobData?.workplaceDetails?.workPlaceId,
      feeConfigs: feeConfigs,
    });

  return (
    <AppColumn>
      <SizedBox height={theme.spacing.elementPadding} />
      <AppRow
        style={{
          alignItems: "center",
        }}
      >
        {jobData?.isEmergency === true && (
          <span
            title={t("jobs.emergency")}
            aria-label={t("jobs.emergency")}
            style={{
              marginRight: theme.spacing.elementPaddingSmall,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Icon
              name="bolt"
              size="large"
              style={{ color: theme.colors.red, margin: 0 }}
            />
          </span>
        )}
        <AppText.BodyText
          use={"h1"}
          margins={{ marginBottom: "0px", marginTop: "0px" }}
          fontWeight={700}
        >
          {profession} {t("to")}{" "}
          {` ${jobCity}   ${jobData?.sponsored === true ? "🏆" : ""} ${dateRange} `}{" "}
          {canDisplayCompensation && ` | ${compensationText}`}
        </AppText.BodyText>
      </AppRow>

      {(isAuthenticated || !staffingWorkplaceData) && (
        <AppColumn>
          <SizedBox height={theme.spacing.elementPaddingSmall} />
          <AppRow crossAxisAlignment={"center"}>
            <AppText.BodyText
              margins={{ marginBottom: "0px", marginTop: "0px" }}
            >
              {[
                ...new Set([
                  actualWorkplaceDetails?.name,
                  actualWorkplaceDetails?.nameVerksamhet,
                ]),
              ].join(", ")}
            </AppText.BodyText>
          </AppRow>
        </AppColumn>
      )}
      <SizedBox height={theme.spacing.elementPaddingSmall} />
      <AppBrowserView>
        <AppText.Caption
          use={"strong"}
          margins={{ marginBottom: "0px", marginTop: "0px" }}
        >
          {jobEmploymentTypes.join(", ")}{" "}
          {showDateRange &&
            ` |  ${t("last_application_date")}: ${showExpireAt ? getShortDate(expireAt, true) : t("not_specified")}`}
        </AppText.Caption>
      </AppBrowserView>
      <MobileView>
        <AppText.BodyText
          use={"h2"}
          margins={{ marginBottom: "0px", marginTop: "0px" }}
        >
          {jobEmploymentTypes.join(", ")}{" "}
          {showDateRange &&
            ` |  ${t("last_application_date")}: ${showExpireAt ? getShortDate(expireAt, true) : t("not_specified")} `}
        </AppText.BodyText>
      </MobileView>
      <SizedBox height={theme.spacing.elementPadding} />

      <PhotoGrid
        photos={photos}
        title={jobTitle}
        onClick={(url) => {
          const currentIndex = photos.findIndex((e) => e === url);

          openModal(MODAL_MEDIA_VIEW, {
            images: photos,
            currentIndex: currentIndex,
          });
        }}
      />

      <SizedBox height={theme.spacing.compactElementPadding} />
    </AppColumn>
  );
}
